import React, { useEffect, useState } from 'react';
import styles from './Students.module.scss';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Search from '../../../components/search/Search';
import Filter from '../../../components/filter/Filter';
import Card from '../../../components/card/Card';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import axiosClient from '../../../api/axiosClient';
import LoadMore from '../../../components/loadMore/loadMore';
import { useToasts } from '../../../contexts/ToastContext';

const Students = () => {  
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('MyStudents');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToasts();

  useEffect(() => {
    // Reset the page when the searchKeyword or selectedFilter changes
    setPage(1);
    setData([]); // Clear previous data
  }, [searchKeyword, selectedFilter]); // Only reset page on filter or search change

  useEffect(() => {
    const params = {
      page: page,
      limit: 20
    };
    if(searchKeyword) {
      params.search = searchKeyword;
    }
    if(selectedFilter) {
      params.filter = selectedFilter;
    }
    const fetchData = async () => {
      if(page === 1) {
        setIsLoading(true);
      }
      await axiosClient.get(`users/instructor/students`, {
        params
      })
      .then ((response) => {
        if(response.data?.students) {
          if(page === 1) {
            setData([]);
          }
          setData((prevData) => {
            return [...prevData, ...response.data.students.map((item) => ({
              id: item.id,
              profilePicture: item?.profilePicture ?? null,
              firstName: item?.firstName ?? '',
              lastName: item?.lastName ?? '',
              data: [
                {
                  label: 'Rotation Type',
                  value: item?.specialities.length > 0 ? 
                    `${item?.specialities[0].name} ${item?.specialities.length > 1 
                      ? `+${item?.specialities.length - 1}` : ''}` : 
                    '',
                  tooltip: item?.specialities.length > 1 ? 
                    item?.specialities.map(item => item.name).join(', ') :
                    ''
                },
                {
                  label: 'Year',
                  value: item?.year?.year ?? ''
                },
                {
                  label: 'Semester',
                  value: item?.semester?.semester ?? ''
                },
                {
                  label: 'Month',
                  value: item?.month?.month ?? ''
                },
              ],
              isAssigned: item?.isAllocated ?? true,
              status: item.status ?? null,
            }))];
          });
          setTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        setLoadMoreLoading(false);
      });
    };

    fetchData();
  }, [searchKeyword, selectedFilter, page, showToast]);

  const loadMore = () => {
    setLoadMoreLoading(true);
    setPage((prevPage) => prevPage + 1);
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle
          label="Student Management"
        />
        <div className={styles.search}>
          <Search
            placeholder="Search by Student Name"
            onSubmit={(keyword) => {
              setSearchKeyword(keyword);
            }}
          />
        </div>
        <div className={styles.filter}>
          <Filter
            filters={[
              {
                label: 'My Students',
                value: 'MyStudents',
              },
              {
                label: 'Other Students',
                value: 'GuestStudents',
              },
              {
                label: 'All Students',
                value: '',
              }
            ]}
            selected={selectedFilter}
            onChange={(filter) => {
              setSelectedFilter(filter);
            }}
          />
        </div>
      </div>
      <div className={styles.container}>
        {data.length > 0 ? <div className={styles.cardList}>
          {data.map((item, index) => {
            return (
              <div className={styles.cardItem} key={`${item.name}-${index}`}>
                <Card 
                  {...item} 
                  onClick={() => {
                    navigate(`/student-overview/${item.id}`);
                  }}
                  showFooter={false}
                />
              </div>
            );
          })}
          {(loadMoreLoading || page < totalPages) && <LoadMore isLoading={loadMoreLoading} onClick={loadMore} />}
        </div> : !isLoading && <div className={styles.emptyList}>
          <p>No students available</p>
        </div>}
      </div>
    </>
  );
}

export default Students;
